import React, { lazy, useContext } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import UserContext from '../../../contexts/UserContext';
import { isDevWhitelisted } from '../../../helpers/devWhitelist';

const ManagePageHeaders = lazy(
  () => import('../../systemAdmin/pageOptions/pageHeaders/ManagePageHeaders')
);
const ManagePopups = lazy(
  () => import('../../systemAdmin/pageOptions/popups/ManagePopups')
);
const ManageUsers = lazy(
  () => import('../../systemAdmin/manageUsers/ManageUsers')
);
const ManagePages = lazy(
  () => import('../../systemAdmin/pageOptions/pages/ManagePages')
);
const ReviewDashboard = lazy(
  () => import('../../systemAdmin/reviews/ReviewDashboard')
);
const PageBuilder = lazy(() => import('../../pageBuilder/builder/PageBuilder'));
const ManageConferences = lazy(
  () =>
    import('../../systemAdmin/sessions/createEditSessions/ManageConferences')
);
const ManageConferenceRecordings = lazy(
  () =>
    import('../../systemAdmin/sessions/recordings/ManageConferenceRecordings')
);
const ManageMedia = lazy(() => import('../../systemAdmin/media/ManageMedia'));
const CompanyIntegrations = lazy(
  () => import('../../company/CompanyIntegrations')
);
const ManageRuleSets = lazy(
  () => import('../../systemAdmin/manageRuleSets/ManageRuleSets')
);
const ManageAccessRules = lazy(() => import('../../company/ManageAccessRules'));

const SignInAsAnotherUser = lazy(
  () => import('../../systemAdmin/signInAsAnotherUser/SigninAsAnotherUser')
);
const CoachesAvailabilityInTier = lazy(
  () =>
    import(
      '../../systemAdmin/coachManagement/coachesAvailabilityInTier/CoachesAvailabilityInTier'
    )
);
const CoachOnboardProfiles = lazy(
  () =>
    import(
      '../../systemAdmin/coachManagement/coachOnboarding/CoachOnboardProfiles'
    )
);
const ManageUser = lazy(
  () => import('../../systemAdmin/manageUsers/manageUser/ManageUser')
);
const ManageProducts = lazy(
  () => import('../../systemAdmin/commerce/manageProducts/ManageProducts')
);
const NotificationBlaster = lazy(
  () => import('../../systemAdmin/notificationBlaster/NotificationBlaster')
);
const ManageDiscounts = lazy(
  () => import('../../systemAdmin/commerce/manageDiscounts/ManageDiscounts')
);
const CourseBuilder = lazy(() => import('../../coursesRebuild/CourseBuilder'));
const STTutorialConfiguration = lazy(
  () =>
    import(
      '../../systemAdmin/statTracker/tutorialConfiguration/STTutorialConfiguration'
    )
);
const Notes = lazy(() => import('../../systemAdmin/pageOptions/notes/Notes'));
const ConferenceAuditPage = lazy(
  () =>
    import(
      '../../systemAdmin/sessions/conferenceAudit/ConferenceAuditPage'
    )
);
const ImpersonationAuditPage = lazy(
  () =>
    import(
      '../../systemAdmin/userManagement/impersonationAudit/ImpersonationAuditPage'
    )
);
const RulesetAuditPage = lazy(
  () =>
    import(
      '../../systemAdmin/manageRuleSets/rulesetAudit/RulesetAuditPage'
    )
);
const RulesetEvaluator = lazy(
  () => import('../../systemAdmin/rulesetEvaluator/RulesetEvaluator')
);

const hasRulesetEvaluatorAccess = (permissions: any[] | undefined) => {
  if (!permissions) return false;
  if (typeof window === 'undefined') return false;
  // Dev-mode gate so only devs can hit the evaluator while it's still
  // burning Keap rate-limit budget on every fresh eval. Backend still
  // enforces admin_role_id IN (1, 15) — this is a UI-only restriction.
  if (localStorage.getItem('cwu_developer_mode') !== 'true') return false;
  return permissions.some(
    (p) => p.admin_role_id === 1 || p.admin_role_id === 15
  );
};

const AdminRoutes = () => {
  const { user_bundle } = useContext(UserContext);
  return (
    <Routes>
      <Route path='/reviews' element={<ReviewDashboard />} />
      <Route path='/pageBuilder/managePages' element={<ManagePages />} />
      <Route
        path='/pageBuilder/managePageHeaders'
        element={<ManagePageHeaders />}
      />
      <Route path='/pageBuilder/managePopups' element={<ManagePopups />} />
      <Route path='/users/manageUsers' element={<ManageUsers />} />
      <Route
        path='/users/signInAsAnotherUser'
        element={<SignInAsAnotherUser />}
      />
      <Route
        path='/adminTools/CoachesAvailableInTier/:path'
        element={<CoachesAvailabilityInTier />}
      />
      <Route
        path='/adminTools/profiles/coaches'
        element={<CoachOnboardProfiles />}
      />
      <Route path='/users/user/:id' element={<ManageUser />} />
      <Route path='/products/manageProducts' element={<ManageProducts />} />
      <Route path='/notifications/blast' element={<NotificationBlaster />} />
      <Route path='/products/discounts' element={<ManageDiscounts />} />
      <Route path='/courses/builder/:id' element={<CourseBuilder />} />
      <Route path='/pageEditor/:id' element={<PageBuilder />} />
      <Route
        path='/conferences/manageSessions'
        element={<ManageConferences />}
      />
      <Route
        path='/conferences/manageRecordings'
        element={<ManageConferenceRecordings />}
      />
      <Route
        path='/conferences/audit'
        element={<ConferenceAuditPage />}
      />
      <Route
        path='/users/signinasanotheruser/audit'
        element={<ImpersonationAuditPage />}
      />
      <Route path='/media/manageMedia' element={<ManageMedia />} />
      <Route
        path='/company/manageIntegrations'
        element={<CompanyIntegrations />}
      />
      <Route
        path='/company/manageRuleSets'
        element={
          isDevWhitelisted(user_bundle?.user)
            ? <ManageRuleSets />
            : <Navigate to='/config' replace />
        }
      />
      <Route
        path='/company/manageAccessRules'
        element={<ManageAccessRules />}
      />

      <Route
        path='/company/rulesets/audit'
        element={<RulesetAuditPage />}
      />
      <Route
        path='/company/rulesets/evaluate'
        element={
          hasRulesetEvaluatorAccess(user_bundle?.permissions)
            ? <RulesetEvaluator />
            : <Navigate to='/config' replace />
        }
      />
      <Route path='/STTutorials' element={<STTutorialConfiguration />} />
      <Route path='/notes' element={<Notes />} />
    </Routes>
  );
};

export default AdminRoutes;
